/**
 * DropZone — drag-and-drop file selector for the ingest upload page.
 *
 * Uses react-dropzone for reliable cross-browser DnD + click-to-browse.
 * Validates:
 *   - accepted MIME types (hard list below)
 *   - per-file size limit (MAX_FILE_SIZE_MB, default 100 MB)
 *   - total batch file count (MAX_BATCH_FILES, default 100)
 *
 * Rejected files surface a brief inline toast; callers receive only
 * accepted files via `onFilesAccepted`.
 *
 * Copy is intentionally non-technical (non-tech audience per spec).
 */

import { useCallback, useState } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import {
  FileText,
  FileImage,
  FileSpreadsheet,
  File,
  UploadCloud,
  AlertCircle,
  X,
} from 'lucide-react'
import { cn } from '../../../lib/utils'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum single-file size accepted at the dropzone level (100 MB) */
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024

/** Maximum number of files per batch */
export const MAX_BATCH_FILES = 100

/** MIME types the pipeline supports */
const ACCEPTED_MIME: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/html': ['.html', '.htm'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  'image/tiff': ['.tiff', '.tif'],
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function iconForMime(mime: string): React.ReactNode {
  if (mime.startsWith('image/')) return <FileImage className="h-4 w-4" aria-hidden="true" />
  if (mime.includes('spreadsheet') || mime.includes('xlsx'))
    return <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
  if (mime.includes('pdf') || mime.includes('word') || mime.includes('presentation'))
    return <FileText className="h-4 w-4" aria-hidden="true" />
  return <File className="h-4 w-4" aria-hidden="true" />
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface DropZoneProps {
  /** Called with accepted File list (may be appended to existing queue) */
  onFilesAccepted: (files: File[]) => void
  /** Files already queued — shown in the list below the drop area */
  queuedFiles?: File[]
  /** Remove a file from the queue before upload starts */
  onRemoveFile?: (index: number) => void
  /** Whether upload is in progress (disables DnD during upload) */
  uploading?: boolean
  isDark?: boolean
}

interface RejectionToast {
  filename: string
  reason: string
}

export function DropZone({
  onFilesAccepted,
  queuedFiles = [],
  onRemoveFile,
  uploading = false,
  isDark = false,
}: DropZoneProps) {
  const { t } = useTranslation('ingest')
  const [rejections, setRejections] = useState<RejectionToast[]>([])

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      // Enforce batch limit on top of react-dropzone validation
      const available = MAX_BATCH_FILES - queuedFiles.length
      const toAdd = accepted.slice(0, available)

      if (toAdd.length > 0) onFilesAccepted(toAdd)

      // Build rejection toasts
      const toasts: RejectionToast[] = rejected.map((r) => {
        const code = r.errors[0]?.code ?? 'unknown'
        let reason: string
        if (code === 'file-too-large') {
          reason = t('errors.too_large', { max: MAX_FILE_SIZE_BYTES / (1024 * 1024) })
        } else if (code === 'file-invalid-type') {
          reason = t('errors.unsupported_mime', { type: r.file.type || r.file.name.split('.').pop() })
        } else {
          reason = t('errors.generic')
        }
        return { filename: r.file.name, reason }
      })

      // Also toast files that exceed the batch limit
      accepted.slice(available).forEach((f) => {
        toasts.push({ filename: f.name, reason: `Batch limit of ${MAX_BATCH_FILES} files reached` })
      })

      if (toasts.length > 0) {
        setRejections(toasts)
        // Auto-dismiss after 6 seconds
        setTimeout(() => setRejections([]), 6000)
      }
    },
    [onFilesAccepted, queuedFiles.length, t],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_MIME,
    maxSize: MAX_FILE_SIZE_BYTES,
    disabled: uploading,
    multiple: true,
  })

  return (
    <div className="space-y-3">
      {/* Drop area */}
      <div
        {...getRootProps()}
        aria-label={t('upload.dropzone')}
        aria-describedby="dropzone-description dropzone-max-size"
        role="button"
        tabIndex={0}
        className={cn(
          'relative flex min-h-[180px] cursor-pointer flex-col items-center justify-center gap-3',
          'rounded-xl border-2 border-dashed px-6 py-10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          isDragActive
            ? isDark
              ? 'border-brand-400 bg-brand-900/20'
              : 'border-brand-500 bg-brand-50'
            : isDark
              ? 'border-neutral-700 bg-surface-2 hover:border-neutral-500 hover:bg-surface-3'
              : 'border-neutral-200 bg-neutral-50 hover:border-neutral-400 hover:bg-neutral-100',
          uploading && 'cursor-not-allowed opacity-50',
        )}
      >
        <input
          {...getInputProps()}
          aria-label={t('upload.button')}
          aria-describedby="dropzone-description dropzone-max-size"
        />

        <UploadCloud
          className={cn(
            'h-10 w-10',
            isDragActive
              ? 'text-brand-400'
              : isDark
                ? 'text-neutral-500'
                : 'text-neutral-400',
          )}
          aria-hidden="true"
        />

        <div className="text-center">
          <p className={cn('text-sm font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
            {isDragActive ? t('upload.dropzone_active') : t('upload.dropzone')}
          </p>
          <p
            id="dropzone-description"
            className={cn('mt-1 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-500')}
          >
            {t('upload.description')}
          </p>
        </div>

        <p
          id="dropzone-max-size"
          className={cn('text-[11px]', isDark ? 'text-neutral-600' : 'text-neutral-400')}
        >
          {t('upload.max_size', { size: MAX_FILE_SIZE_BYTES / (1024 * 1024) })}
        </p>
      </div>

      {/* Rejection toasts */}
      {rejections.length > 0 && (
        <div
          role="alert"
          aria-live="assertive"
          className={cn(
            'rounded-lg border px-3 py-2.5 space-y-1',
            isDark
              ? 'border-red-800/50 bg-red-900/20 text-red-300'
              : 'border-red-200 bg-red-50 text-red-700',
          )}
        >
          <div className="flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            <span className="text-xs font-medium">
              {rejections.length === 1 ? '1 file could not be added' : `${rejections.length} files could not be added`}
            </span>
          </div>
          {rejections.map((r, i) => (
            <p key={i} className="text-[11px] pl-5">
              <span className="font-medium">{r.filename}</span> — {r.reason}
            </p>
          ))}
        </div>
      )}

      {/* File queue list */}
      {queuedFiles.length > 0 && (
        <ul
          aria-label={`${queuedFiles.length} ${queuedFiles.length === 1 ? 'file' : 'files'} ready to upload`}
          className="space-y-1"
        >
          {queuedFiles.map((file, i) => (
            <li
              key={`${file.name}-${i}`}
              className={cn(
                'flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs',
                isDark ? 'bg-surface-3 text-neutral-300' : 'bg-neutral-100 text-neutral-700',
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className={cn(isDark ? 'text-neutral-500' : 'text-neutral-400')}
                >
                  {iconForMime(file.type)}
                </span>
                <span className="truncate font-medium">{file.name}</span>
                <span className={cn('flex-shrink-0', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
                  {formatBytes(file.size)}
                </span>
              </div>

              {onRemoveFile && !uploading && (
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onRemoveFile(i)}
                  className={cn(
                    'flex-shrink-0 rounded p-0.5 transition-colors',
                    isDark
                      ? 'text-neutral-500 hover:bg-surface-4 hover:text-neutral-300'
                      : 'text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600',
                  )}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
